import React, { useState, useEffect } from 'react';
import {
  User,
  Volume2,
  Award,
  Zap,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  LogIn,
  LogOut,
  UserPlus,
  Trash2,
  Save,
  Sparkles,
  KeyRound,
  Lock,
  Mail,
  X,
  Globe,
  Scale,
  Shield,
  ChevronRight,
  Camera,
  Upload,
  BookOpen,
  Users,
  Check,
  UserCheck,
  UserX,
  Church,
  MapPin,
  Eye,
  EyeOff,
  Loader2,
} from 'lucide-react';
import { EmailVerificationView } from './EmailVerificationView.tsx';
import { TermsOfUseView } from './legal/TermsOfUseView.tsx';
import { PrivacyNoticeView } from './legal/PrivacyNoticeView.tsx';
import { CommunityGuidelinesView } from './legal/CommunityGuidelinesView.tsx';
import { supabase, getSupabaseClient } from '../lib/supabase.ts';
import {
  LANGUAGE_META,
  SupportedLanguage,
  UI_TRANSLATIONS,
} from '../i18n/translations.ts';
import { SEED_ACHIEVEMENTS } from '../data/verifiedQuestions.ts';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import { getCountryInfo, COUNTRIES } from '../utils/countryHelper.ts';
import { getCleanAvatarUrl } from '../utils/avatar.ts';
import { useProfile } from '../context/ProfileContext.tsx';
import {
  calculateLevelFromXp,
  formatLifetimeAccuracy,
  formatDisplayNumber,
  getLevelProgressTooltipText,
  getLifetimeAccuracyTooltipText,
} from '../config/scoringConfig.ts';
import { InfoTooltip } from './ui/InfoTooltip.tsx';
import { UserAvatar } from './UserAvatar.tsx';


interface ProfileAndAdminViewProps {
  account: any;
  supabaseUser?: any;
  language: SupportedLanguage;
  theme: 'dark' | 'light';
  onLanguageChange: (lang: SupportedLanguage) => void;
  onThemeChange: (theme: 'dark' | 'light') => void;
  onSessionEstablished?: (session: any, account: any) => void;
  handleActiveSession?: (session: any, account?: any) => void;
  onAccountUpdated: (newAccountData: any) => void;
  onAccountReset?: () => void;
  initialSubTab?: 'profile' | 'audio' | 'achievements' | 'admin';
  onNavigateToLegal?: (section: 'terms' | 'privacy' | 'guidelines') => void;
  onOpenReport?: (target: {
    type: 'profile_picture' | 'feed_post' | 'feed_comment';
    id: string;
    targetUserUid: string;
    label?: string;
  }) => void;
  onOpenUserProfile?: (uidOrUsername: string) => void;
  showToast: (msg: string, type?: 'info' | 'success' | 'error') => void;
}

export const ProfileAndAdminView: React.FC<ProfileAndAdminViewProps> = ({
  account,
  supabaseUser,
  language,
  theme,
  onLanguageChange,
  onSessionEstablished,
  handleActiveSession,
  onAccountUpdated,
  onAccountReset,
  onNavigateToLegal,
  onOpenUserProfile,
  showToast,
}) => {
  const { updateAvatarUrl, refreshProfile } = useProfile();
  const t = UI_TRANSLATIONS[language] || UI_TRANSLATIONS.en;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('qb_auth_token') || '';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token && !token.startsWith('auth-active-') && !token.startsWith('verified-')) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  const fallbackUserFromSession = supabaseUser
    ? {
        uid: supabaseUser.id,
        email: supabaseUser.email,
        displayName:
          supabaseUser.user_metadata?.display_name ||
          supabaseUser.email?.split('@')[0] ||
          'Scholar',
        username:
          supabaseUser.user_metadata?.username ||
          supabaseUser.email?.split('@')[0] ||
          'scholar',
        avatarUrl: supabaseUser.user_metadata?.avatar_url || '',
        country: supabaseUser.user_metadata?.country || 'PH',
        role: 'scholar',
        xp: 0,
        level: 1,
      }
    : {};

  const user = account?.user?.email
    ? account.user
    : { ...fallbackUserFromSession, ...(account?.user || {}) };

  const profile = account?.profile || {};
  const prefs = account?.preferences || {};
  const stats = account?.statistics || {};
  const unlockedBadges: string[] = account?.achievements || [];
  const computedLevelInfo = calculateLevelFromXp(Number(user.xp || 0));
  const levelInfo = {
    ...computedLevelInfo,
    ...(account?.levelInfo || {}),
    summaryText:
      account?.levelInfo?.summaryText || computedLevelInfo.summaryText,
    rankTitle:
      account?.levelInfo?.rankTitle || computedLevelInfo.rankTitle,
  };
  const lifetimeAccInfo =
    account?.lifetimeAccuracy ||
    formatLifetimeAccuracy(
      Number(stats.correctAnswers || 0),
      Number(stats.totalQuestionsAnswered || 0)
    );

  const isRegisteredUserEmail = (email?: string | null) => {
    if (!email) return false;
    const clean = String(email).trim().toLowerCase();
    return (
      clean.includes('@') &&
      !clean.endsWith('@quickbible.global') &&
      !clean.endsWith('@quickybible.global')
    );
  };

  // Determine if user has a registered/authenticated account (Supabase Auth)
  const isRegisteredAccount = Boolean(
    (supabaseUser && isRegisteredUserEmail(supabaseUser.email)) ||
      (user.email && isRegisteredUserEmail(user.email))
  );

  // Modal navigation state for native-app feel
  const [activeModal, setActiveModal] = useState<
    | null
    | 'account'
    | 'audio'
    | 'language'
    | 'achievements'
    | 'progression'
    | 'friends'
    | 'admin'
    | 'legal'
    | 'auth_signup'
    | 'auth_signin'
  >(null);

  const [legalSubTab, setLegalSubTab] = useState<'terms' | 'privacy' | 'guidelines'>('terms');
  const [friendsSubTab, setFriendsSubTab] = useState<'requests' | 'friends'>('friends');

  // Form states
  const [username, setUsername] = useState(user.username || 'Scholar');
  const [displayName, setDisplayName] = useState(
    user.displayName || 'Scripture Scholar'
  );
  const [country, setCountry] = useState(user.country || 'PH');
  const [favoriteVerse, setFavoriteVerse] = useState(
    profile.favoriteVerse || 'Psalm 119:105'
  );
  const [churchOrOrg, setChurchOrOrg] = useState(
    profile.church || profile.churchOrOrg || 'Global Berean Community'
  );
  const [church, setChurch] = useState(
    profile.church || profile.churchOrOrg || 'Global Berean Community'
  );
  const [city, setCity] = useState(profile.city || '');
  const [hideChurch, setHideChurch] = useState(Boolean(profile.hideChurch));
  const [hideLocation, setHideLocation] = useState(Boolean(profile.hideLocation));

  // Friends & Requests state
  const [friendsList, setFriendsList] = useState<any[]>([]);
  const [friendRequests, setFriendRequests] = useState<any[]>([]);
  const [isLoadingFriends, setIsLoadingFriends] = useState(false);

  const [sfxEnabled, setSfxEnabled] = useState(prefs.sfxEnabled ?? true);
  const [bgMusicEnabled, setBgMusicEnabled] = useState(
    prefs.bgMusicEnabled ?? false
  );
  const [bgMusicVolume, setBgMusicVolume] = useState(
    prefs.bgMusicVolume ?? 40
  );
  const [narrationEnabled, setNarrationEnabled] = useState(
    prefs.narrationEnabled ?? false
  );
  const [narrationVoice, setNarrationVoice] = useState(
    prefs.narrationVoice || 'Kore'
  );
  const [narrationSpeed, setNarrationSpeed] = useState(
    prefs.narrationSpeed || '1.0'
  );

  // Auth form state (Sign In / Sign Up modal)
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authUsername, setAuthUsername] = useState('');
  const [authDisplayName, setAuthDisplayName] = useState('');
  const [isSubmittingAuth, setIsSubmittingAuth] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleConfirmDeleteAccount = async () => {
    if (!account?.user?.uid) return;
    setIsDeletingAccount(true);
    try {
      const response = await fetch('/api/auth/delete-account', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ uid: account.user.uid }),
      });
      if (response.ok) {
        if (onAccountReset) onAccountReset();
        setShowDeleteConfirmModal(false);
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to delete account');
      }
    } catch (err) {
      alert('An error occurred while deleting your account.');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const [saveBanner, setSaveBanner] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Admin state
  const [adminData, setAdminData] = useState<any | null>(null);
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  useEffect(() => {
    if (user.username) setUsername(user.username);
    if (user.displayName) setDisplayName(user.displayName);
    if (user.country) setCountry(user.country);
  }, [user.uid, user.username, user.displayName, user.country]);

  useEffect(() => {
    if (profile.church || profile.churchOrOrg) {
      setChurch(profile.church || profile.churchOrOrg);
      setChurchOrOrg(profile.church || profile.churchOrOrg);
    }
    if (profile.city !== undefined) setCity(profile.city || '');
    if (profile.hideChurch !== undefined) setHideChurch(Boolean(profile.hideChurch));
    if (profile.hideLocation !== undefined) setHideLocation(Boolean(profile.hideLocation));
  }, [profile.church, profile.churchOrOrg, profile.city, profile.hideChurch, profile.hideLocation]);

  const loadFriendsAndRequests = async () => {
    if (!user.uid) return;
    setIsLoadingFriends(true);
    try {
      const res = await fetch(`/api/friends?uid=${encodeURIComponent(user.uid)}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setFriendsList(data.friends || []);
        setFriendRequests(data.requests || []);
      }
    } catch (err) {
      console.error('Failed to load friends list:', err);
    } finally {
      setIsLoadingFriends(false);
    }
  };

  useEffect(() => {
    if (user.uid) {
      loadFriendsAndRequests();
    }
  }, [user.uid]);

  const handleRespondFriendRequest = async (requestId: string, action: 'accept' | 'decline') => {
    if (!user.uid) return;
    try {
      const res = await fetch('/api/friends/respond', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ uid: user.uid, requestId, action }),
      });
      if (res.ok) {
        soundEngine.playCorrect();
        await loadFriendsAndRequests();
      }
    } catch (err) {
      console.error('Failed to respond to friend request:', err);
    }
  };

  const handleUnfriend = async (friendshipId: string) => {
    if (!user.uid) return;
    try {
      const res = await fetch('/api/friends/unfriend', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ uid: user.uid, friendshipId }),
      });
      if (res.ok) {
        soundEngine.playClick();
        await loadFriendsAndRequests();
      }
    } catch (err) {
      console.error('Failed to unfriend:', err);
    }
  };

  useEffect(() => {
    if (isRegisteredAccount) {
      if (activeModal === 'auth_signin' || activeModal === 'auth_signup') {
        setActiveModal(null);
      }
      setUnverifiedEmail(null);
    }
  }, [isRegisteredAccount, activeModal]);

  const fetchAdminOverview = async () => {
    setLoadingAdmin(true);
    try {
      const res = await fetch('/api/admin/overview', {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setAdminData(data);
      }
    } finally {
      setLoadingAdmin(false);
    }
  };

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);
    setSaveBanner(null);

    const validMimes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    const hasValidExt = /\.(png|jpe?g|gif|webp)$/i.test(file.name);
    if (!validMimes.includes(file.type) && !hasValidExt) {
      setAvatarError(
        'Invalid file type. Please upload a PNG, JPG, GIF, or WebP image.'
      );
      return;
    }

    const maxBytes = 4 * 1024 * 1024;
    if (file.size > maxBytes) {
      setAvatarError(
        `File size (${(file.size / (1024 * 1024)).toFixed(1)} MB) exceeds 4 MB. Please upload an image under 4 MB.`
      );
      return;
    }

    const userId = user?.uid || supabaseUser?.id;
    if (!userId) {
      setAvatarError('User ID not found. Please sign in to upload an avatar.');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const fileExt = (file.name.split('.').pop() || 'png').toLowerCase();
      const filePath = `${userId}/avatar.${fileExt}`;
      const supaClient = getSupabaseClient();

      console.log('[Avatar Upload] Uploading file to Supabase Storage avatars bucket path:', filePath);
      
      // 1. Upload image to PUBLIC Supabase Storage bucket "avatars" with upsert: true
      const { data: uploadData, error: uploadError } = await supaClient.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      // Requirement 7: Console log upload result
      console.log('[Avatar Upload] Upload result:', uploadData, uploadError);

      let publicUrl = '';
      if (!uploadError) {
        // Requirement 2: Get public URL with getPublicUrl (NOT createSignedUrl, NOT a blob/object URL)
        const { data: urlData } = supaClient.storage
          .from('avatars')
          .getPublicUrl(filePath);
        publicUrl = urlData?.publicUrl || '';
      }

      const cleanPublicUrl = getCleanAvatarUrl(publicUrl || '');
      // Requirement 7: Console log public URL
      console.log('[Avatar Upload] Public URL:', cleanPublicUrl);

      let dbUpdateResult: { data: any; error: any } = { data: null, error: null };
      if (cleanPublicUrl) {
        // Requirement 2: Save to profiles.avatar_url via UPDATE ... WHERE id = auth.uid()
        const updateRes = await supaClient
          .from('profiles')
          .update({ avatar_url: cleanPublicUrl, updated_at: new Date().toISOString() })
          .or(`uid.eq.${userId},user_id.eq.${userId}`);
        
        dbUpdateResult = { data: updateRes.data, error: updateRes.error };
        // Requirement 7: Console log DB update response
        console.log('[Avatar Upload] DB update response (profiles):', dbUpdateResult.data, dbUpdateResult.error);

        if (updateRes.error) {
          console.warn('[Avatar Upload] Failed to update profiles table directly:', updateRes.error.message);
        }
      }

      // 2. Sync through backend API endpoint
      const reader = new FileReader();
      const dataUrlPromise = new Promise<string>((resolve) => {
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      const dataUrl = await dataUrlPromise;

      const res = await fetch('/api/profile/avatar', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          uid: userId,
          avatarUrl: cleanPublicUrl,
          avatarDataUrl: dataUrl,
        }),
      });

      const resData = await res.json();
      if (!res.ok && !cleanPublicUrl) {
        setAvatarError(resData.error || 'Failed to update profile picture.');
        return;
      }

      const finalAvatarUrl = getCleanAvatarUrl(resData.avatarUrl || cleanPublicUrl || '');

      if (finalAvatarUrl) {
        updateAvatarUrl(finalAvatarUrl);
        await refreshProfile().catch(() => {});
        onAccountUpdated({
          ...account,
          user: {
            ...(account?.user || {}),
            avatarUrl: finalAvatarUrl,
          },
          profile: {
            ...(account?.profile || {}),
            avatarUrl: finalAvatarUrl,
          },
        });
      }

      soundEngine.playClick();
      showToast('Profile picture updated successfully!', 'success');
    } catch (err: any) {
      console.error('[Avatar Upload] Error:', err);
      setAvatarError(err.message || 'Error updating profile picture.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleEmailAuthSubmit = async (e: React.FormEvent, mode: 'signup' | 'signin') => {
    e.preventDefault();
    setAuthError(null);
    setSaveBanner(null);

    const cleanEmail = authEmail.trim().toLowerCase();
    if (!cleanEmail) {
      setAuthError('Please enter your email address.');
      return;
    }
    if (!authPassword || authPassword.length < 6) {
      setAuthError('Password must be at least 6 characters long.');
      return;
    }

    let finalUsername = '';
    if (mode === 'signup') {
      finalUsername = (authUsername.trim() || authDisplayName.trim().replace(/\s+/g, '') || cleanEmail.split('@')[0]).trim();
      if (!finalUsername || finalUsername.length < 3 || !/^[a-zA-Z0-9_]+$/.test(finalUsername)) {
        setAuthError('Please choose a valid username (at least 3 characters, letters, numbers, underscores).');
        return;
      }
      if (!consentAgreed) {
        setAuthError('Please agree to the Terms of Use and Privacy Notice to register.');
        return;
      }
    }

    setIsSubmittingAuth(true);
    try {
      if (mode === 'signup') {
        const acceptedAt = new Date().toISOString();
        const res = await fetch('/api/auth/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: cleanEmail,
            password: authPassword,
            username: finalUsername,
            displayName: authDisplayName.trim() || finalUsername,
            country,
            language,
            theme,
            termsAcceptedAt: acceptedAt,
            termsVersion: '2026.1',
            privacyVersion: '2026.1',
          }),
        });

        const data = await res.json();
        if (!res.ok) {
          setAuthError(data?.error || 'Could not register account.');
          return;
        }

        // Keep authPassword in state so OTP verification can use signInWithPassword fallback if needed
        setUnverifiedEmail(cleanEmail);
        setSaveBanner('Registration successful! Check your email for the 6-digit verification code.');
        return;
      } else {
        const res = await fetch('/api/auth/signin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: cleanEmail,
            password: authPassword,
            language,
            theme,
          }),
        });

        const data = await res.json();
        console.log('[Auth Sign-In Result] Response:', res.status, data);

        if (!res.ok) {
          if (data?.requiresVerification || data?.unverified) {
            setUnverifiedEmail(cleanEmail);
            setAuthError(data?.error || 'Email unverified. Please enter your 6-digit code.');
            return;
          }
          setAuthError(data?.error || 'Invalid email or password.');
          return;
        }

        let activeSession = data?.session ?? null;

        if (activeSession?.access_token && activeSession?.refresh_token) {
          localStorage.setItem('qb_auth_token', activeSession.access_token);
          console.log('[Auth Session] Setting session in Supabase Auth client...');
          try {
            const setRes = await supabase.auth.setSession({
              access_token: activeSession.access_token,
              refresh_token: activeSession.refresh_token,
            });
            if (setRes?.data?.session) {
              activeSession = setRes.data.session;
            }
          } catch (err: any) {
            console.error('[Auth Session] setSession error:', err?.message || err);
          }
        } else {
          localStorage.setItem('qb_auth_token', `auth-active-${Date.now()}`);
        }

        if (!activeSession) {
          try {
            const getSesRes = await supabase.auth.getSession();
            activeSession = getSesRes?.data?.session ?? null;
          } catch {}
        }

        if (data?.account?.user) {
          if (data.account.user.uid) {
            localStorage.setItem('qb_scholar_uid', data.account.user.uid);
          }
          onAccountUpdated(data.account);
          setUsername(data.account.user.username || cleanEmail.split('@')[0]);
          setDisplayName(data.account.user.displayName || data.account.user.username);
        }
        setAuthPassword('');
        setActiveModal(null);
        setSaveBanner(`Signed in successfully as ${cleanEmail}.`);

        const sessionToApply = activeSession || data?.session || null;
        if (handleActiveSession) {
          handleActiveSession(sessionToApply, data?.account ?? null);
        } else if (onSessionEstablished) {
          onSessionEstablished(sessionToApply, data?.account ?? null);
        }
      }
    } catch (err: any) {
      console.error('[Auth Sign-In Error]', err);
      setAuthError(err.message || 'Authentication request failed.');
    } finally {
      setIsSubmittingAuth(false);
    }
  };

  const handleForgotPassword = async () => {
    setAuthError(null);
    setSaveBanner(null);
    const cleanEmail = authEmail.trim().toLowerCase();
    if (!cleanEmail) {
      setAuthError('Please enter your email address first.');
      return;
    }
    setIsSubmittingAuth(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setSaveBanner(`Password reset link sent to ${cleanEmail}.`);
      } else {
        setAuthError(data.error || 'Failed to send reset link.');
      }
    } catch (err: any) {
      setAuthError(err.message || 'Request failed.');
    } finally {
      setIsSubmittingAuth(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err: any) {
      console.warn('[Sign Out] error:', err?.message || err);
    }
    localStorage.removeItem('qb_auth_token');
    localStorage.removeItem('qb_scholar_uid');
    setActiveModal(null);
    setSaveBanner(null);
    if (onAccountReset) {
      onAccountReset();
    }
  };


  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSavingProfile) return;
    
    soundEngine.playClick();
    setSaveBanner(null);
    setIsSavingProfile(true);

    soundEngine.sfxEnabled = sfxEnabled;
    soundEngine.narrationEnabled = narrationEnabled;
    soundEngine.narrationVoice = narrationVoice;
    soundEngine.narrationSpeed = Number(narrationSpeed) || 1.0;
    soundEngine.toggleBackgroundMusic(bgMusicEnabled, bgMusicVolume / 100);

    try {
      const res = await fetch('/api/profile', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          uid: user.uid,
          username,
          displayName,
          country,
          language,
          theme,
          favoriteVerse,
          churchOrOrg: church,
          church,
          city,
          hideChurch,
          hideLocation,
          sfxEnabled,
          bgMusicEnabled,
          bgMusicVolume,
          narrationEnabled,
          narrationVoice,
          narrationSpeed,
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        onAccountUpdated(updated);
        showToast('Profile updated successfully!', 'success');
        setActiveModal(null);
      } else {
        const errData = await res.json();
        showToast(errData.error || 'Unable to save changes. Please try again.', 'error');
      }
    } catch (err) {
      showToast('Unable to save changes. Please try again.', 'error');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const countryInfo = getCountryInfo(country);
  const isAdmin = user.role === 'admin' || user.email === 'adonizchoy@gmail.com';

  return (
    <div className="max-w-4xl mx-auto py-6 sm:py-10 px-4 sm:px-6 pb-32 md:pb-16 space-y-6">
      {/* ================= 1. GUEST / FIRST-TIME USER PROFILE ================= */}
      {!isRegisteredAccount && (
        <div className="space-y-6">
          <div className="max-w-md mx-auto rounded-3xl bg-[#141414] border border-[#262626] p-8 shadow-2xl text-center space-y-6">
            <div className="w-20 h-20 mx-auto rounded-3xl overflow-hidden shadow-xl border border-amber-500/30">
              <img
                src="/quickibible-icon.png"
                alt="QuickiBible Q! Brand Icon"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white tracking-tight">
                Join QuickiBible
              </h2>
              <p className="text-xs sm:text-sm text-[#D4D4D4] leading-relaxed">
                Create an account to save your progress, earn XP, unlock badges, compete with players worldwide, and enjoy the complete QuickiBible experience.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  soundEngine.playClick();
                  setAuthError(null);
                  setActiveModal('auth_signup');
                }}
                className="w-full py-3.5 px-6 rounded-2xl bg-[#525252] hover:bg-[#404040] text-white font-bold text-sm transition-all shadow-lg shadow-black/40 flex items-center justify-center gap-2 cursor-pointer"
              >
                <UserPlus className="w-4 h-4" />
                <span>Create Account</span>
              </button>

              <div className="text-xs text-[#A3A3A3] pt-1">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    soundEngine.playClick();
                    setAuthError(null);
                    setActiveModal('auth_signin');
                  }}
                  className="text-[#A3A3A3] font-semibold hover:underline cursor-pointer"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>

          {/* Legal Documents & Policies subtle footer link */}
          <div className="text-center pt-4">
            <button
              type="button"
              onClick={() => {
                soundEngine.playClick();
                setLegalSubTab('terms');
                setActiveModal('legal');
              }}
              className="text-xs font-medium text-[#A3A3A3] hover:text-[#A3A3A3] transition-colors inline-flex items-center gap-1 cursor-pointer"
            >
              <span>Legal Documents &amp; Policies</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ================= 2. AUTHENTICATED USER PROFILE ================= */}
      {isRegisteredAccount && (
        <div className="space-y-6">
          {/* Compact Profile Header */}
          <div className="rounded-2xl bg-[#141414] border border-[#262626] p-5 sm:p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[#1C1C1C] border border-[#262626] text-[#A3A3A3] font-bold text-xl flex items-center justify-center shrink-0 shadow-md overflow-hidden select-none">
                <UserAvatar 
                  src={user.avatarUrl} 
                  name={username} 
                  sizeClassName="w-full h-full" 
                  className="rounded-2xl" 
                  versionKey={user.updatedAt || user.uid}
                />
              </div>

              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-lg sm:text-xl font-bold text-white truncate tracking-tight">
                    {displayName}
                  </h1>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-[#262626] text-[#FAFAFA] font-semibold border border-[#525252]">
                    Level {levelInfo.level} · {levelInfo.rankTitle || 'Scripture Seeker'}
                  </span>
                </div>
                <div className="text-xs text-[#A3A3A3] font-mono">
                  @{username} · {user.email}
                </div>
                <div className="text-xs text-[#A3A3A3] flex items-center gap-1">
                  <span>{countryInfo.flag}</span>
                  <span>{countryInfo.name}</span>
                </div>
              </div>
            </div>

            {/* Level & XP Progress Bar: "Level 4 · 120 / 200 to next" */}
            <div className="space-y-1.5 pt-2 border-t border-[#262626]">
              <div className="flex items-center justify-between text-xs gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span
                    data-testid="profile-level-summary"
                    className="font-semibold text-[#D4D4D4] font-mono"
                  >
                    {levelInfo.summaryText}
                  </span>
                  <InfoTooltip
                    ariaLabel={t.ariaHowLevelCalculated || 'How Level is calculated'}
                    content={getLevelProgressTooltipText(t)}
                  />
                </div>
                <span
                  className={`font-mono font-bold tabular-nums ${
                    formatDisplayNumber(user.xp || 0).isZero
                      ? 'text-[#737373]'
                      : 'text-[#A3A3A3]'
                  }`}
                >
                  {formatDisplayNumber(user.xp || 0).isZero
                    ? '–'
                    : `${formatDisplayNumber(user.xp || 0).text} XP`}
                </span>
              </div>
              <div className="w-full h-2 rounded-full bg-[#1C1C1C] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#FF8A00] to-[#EA580C] rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.max(4, levelInfo.progressPercent))}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-[11px] text-[#A3A3A3] pt-0.5">
                <div className="flex items-center gap-1.5">
                  <span>Lifetime Accuracy:</span>
                  <InfoTooltip
                    ariaLabel={t.ariaHowLifetimeAccuracyCalculated || 'How Lifetime Accuracy is calculated'}
                    content={getLifetimeAccuracyTooltipText(t)}
                  />
                </div>
                <span
                  data-testid="profile-lifetime-accuracy"
                  className={`font-mono ${
                    lifetimeAccInfo.unlocked ? 'text-emerald-400 font-bold' : 'text-[#737373]'
                  }`}
                >
                  {lifetimeAccInfo.label}
                </span>
              </div>
            </div>

          </div>

          {/* ================= 3. NATIVE-STYLE PROFILE MENU ================= */}
          <div className="space-y-4">
            {/* ACCOUNT */}
            <div className="space-y-2">
              <div className="text-[11px] font-mono font-semibold uppercase tracking-wider text-[#A3A3A3] px-1">
                Account
              </div>
              <div className="rounded-2xl bg-[#141414] border border-[#262626] divide-y divide-[#262626] overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    soundEngine.playClick();
                    setActiveModal('account');
                  }}
                  className="w-full p-4 flex items-center justify-between hover:bg-[#1C1C1C] transition-colors text-left group cursor-pointer"
                >
                  <div>
                    <div className="text-sm font-semibold text-white group-hover:text-[#A3A3A3] transition-colors">
                      Account &amp; Settings
                    </div>
                    <div className="text-xs text-[#A3A3A3] mt-0.5">
                      Manage profile information, photo and account preferences.
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#737373] group-hover:text-[#A3A3A3] group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              </div>
            </div>

            {/* PREFERENCES */}
            <div className="space-y-2">
              <div className="text-[11px] font-mono font-semibold uppercase tracking-wider text-[#A3A3A3] px-1">
                Preferences
              </div>
              <div className="rounded-2xl bg-[#141414] border border-[#262626] divide-y divide-[#262626] overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    soundEngine.playClick();
                    setActiveModal('audio');
                  }}
                  className="w-full p-4 flex items-center justify-between hover:bg-[#1C1C1C] transition-colors text-left group cursor-pointer"
                >
                  <div>
                    <div className="text-sm font-semibold text-white group-hover:text-[#A3A3A3] transition-colors">
                      Audio &amp; Narration
                    </div>
                    <div className="text-xs text-[#A3A3A3] mt-0.5">
                      Manage narration, sounds, and audio preferences.
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#737373] group-hover:text-[#A3A3A3] group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    soundEngine.playClick();
                    setActiveModal('language');
                  }}
                  className="w-full p-4 flex items-center justify-between hover:bg-[#1C1C1C] transition-colors text-left group cursor-pointer"
                >
                  <div>
                    <div className="text-sm font-semibold text-white group-hover:text-[#A3A3A3] transition-colors">
                      Language
                    </div>
                    <div className="text-xs text-[#A3A3A3] mt-0.5">
                      Manage interface and Bible language preferences.
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#737373] group-hover:text-[#A3A3A3] group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              </div>
            </div>

            {/* ACHIEVEMENTS */}
            <div className="space-y-2">
              <div className="text-[11px] font-mono font-semibold uppercase tracking-wider text-[#A3A3A3] px-1">
                Achievements
              </div>
              <div className="rounded-2xl bg-[#141414] border border-[#262626] divide-y divide-[#262626] overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    soundEngine.playClick();
                    setActiveModal('achievements');
                  }}
                  className="w-full p-4 flex items-center justify-between hover:bg-[#1C1C1C] transition-colors text-left group cursor-pointer"
                >
                  <div>
                    <div className="text-sm font-semibold text-white group-hover:text-[#A3A3A3] transition-colors">
                      Badges &amp; Achievements ({unlockedBadges.length}/{SEED_ACHIEVEMENTS.length})
                    </div>
                    <div className="text-xs text-[#A3A3A3] mt-0.5">
                      View unlocked badges and accomplishments.
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#737373] group-hover:text-[#A3A3A3] group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    soundEngine.playClick();
                    setActiveModal('progression');
                  }}
                  className="w-full p-4 flex items-center justify-between hover:bg-[#1C1C1C] transition-colors text-left group cursor-pointer"
                >
                  <div>
                    <div className="text-sm font-semibold text-white group-hover:text-[#A3A3A3] transition-colors">
                      Scholar Progression
                    </div>
                    <div className="text-xs text-[#A3A3A3] mt-0.5">
                      View level, XP, and learning progress.
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#737373] group-hover:text-[#A3A3A3] group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              </div>
            </div>

            {/* FELLOWSHIP & FRIENDS */}
            <div className="space-y-2">
              <div className="text-[11px] font-mono font-semibold uppercase tracking-wider text-[#A3A3A3] px-1 flex items-center justify-between">
                <span>Fellowship &amp; Friends</span>
                {friendRequests.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-mono border border-amber-500/30 font-bold">
                    {friendRequests.length} pending
                  </span>
                )}
              </div>
              <div className="rounded-2xl bg-[#141414] border border-[#262626] divide-y divide-[#262626] overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    soundEngine.playClick();
                    loadFriendsAndRequests();
                    setActiveModal('friends');
                  }}
                  className="w-full p-4 flex items-center justify-between hover:bg-[#1C1C1C] transition-colors text-left group cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#1C1C1C] border border-[#262626] flex items-center justify-center text-amber-500 group-hover:scale-105 transition-transform">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white group-hover:text-amber-400 transition-colors flex items-center gap-2">
                        <span>Friends &amp; Requests</span>
                        {friendRequests.length > 0 && (
                          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                        )}
                      </div>
                      <div className="text-xs text-[#A3A3A3] mt-0.5">
                        {friendsList.length} {friendsList.length === 1 ? 'friend' : 'friends'} • {friendRequests.length} pending {friendRequests.length === 1 ? 'request' : 'requests'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#737373] group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              </div>
            </div>

            {/* ADMIN CONSOLE (If Admin) */}
            {isAdmin && (
              <div className="space-y-2">
                <div className="text-[11px] font-mono font-semibold uppercase tracking-wider text-amber-500 px-1">
                  Administration
                </div>
                <div className="rounded-2xl bg-[#141414] border border-amber-900/40 divide-y divide-[#262626] overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      soundEngine.playClick();
                      fetchAdminOverview();
                      setActiveModal('admin');
                    }}
                    className="w-full p-4 flex items-center justify-between hover:bg-amber-500/5 transition-colors text-left group cursor-pointer"
                  >
                    <div>
                      <div className="text-sm font-semibold text-amber-400 group-hover:text-amber-300 transition-colors flex items-center gap-1.5">
                        <ShieldCheck className="w-4 h-4 text-amber-500" />
                        <span>Admin Console</span>
                      </div>
                      <div className="text-xs text-[#A3A3A3] mt-0.5">
                        Validated question pool &amp; user moderation.
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-amber-500/60 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>
                </div>
              </div>
            )}

            {/* Legal Documents & Policies Link & Sign Out */}
            <div className="pt-2 flex items-center justify-between px-1">
              <button
                type="button"
                onClick={() => {
                  soundEngine.playClick();
                  setLegalSubTab('terms');
                  setActiveModal('legal');
                }}
                className="text-xs font-medium text-[#A3A3A3] hover:text-[#A3A3A3] transition-colors inline-flex items-center gap-1 cursor-pointer"
              >
                <span>Legal Documents &amp; Policies</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={handleSignOut}
                className="text-xs font-semibold text-red-400 hover:text-red-300 transition-colors inline-flex items-center gap-1 cursor-pointer"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span>{t.signOut}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {saveBanner && (
        <div role="status" className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-300">
          {saveBanner}
        </div>
      )}

      {/* ================= MODALS / SUB-SCREENS (NATIVE APP FEEL) ================= */}

      {/* 1. AUTH MODAL (SIGN UP / SIGN IN) */}
      {(activeModal === 'auth_signup' || activeModal === 'auth_signin') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-lg rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl space-y-6 text-[#FAFAFA] max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            {unverifiedEmail ? (
              <EmailVerificationView
                email={unverifiedEmail}
                password={authPassword}
                onVerified={async (verifiedData) => {
                  try {
                    const verifiedEmailAddr = unverifiedEmail;
                    let activeSession = verifiedData?.session ?? null;
                    let activeAccount = verifiedData?.account ?? null;

                    if (activeSession?.access_token && activeSession?.refresh_token) {
                      localStorage.setItem('qb_auth_token', activeSession.access_token);
                      try {
                        const setRes = await supabase.auth.setSession({
                          access_token: activeSession.access_token,
                          refresh_token: activeSession.refresh_token,
                        });
                        if (setRes?.data?.session) {
                          activeSession = setRes.data.session;
                        }
                      } catch {}
                    }

                    // Fallback 1: getSession() if session is null
                    if (!activeSession) {
                      try {
                        const getSesRes = await supabase.auth.getSession();
                        activeSession = getSesRes?.data?.session ?? null;
                        console.log('[OTP Verify] onVerified getSession() fallback:', Boolean(activeSession));
                      } catch {}
                    }

                    // Fallback 2: signInWithPassword if still no session and password was entered
                    if (!activeSession && authPassword && verifiedEmailAddr) {
                      try {
                        const signInRes = await supabase.auth.signInWithPassword({
                          email: verifiedEmailAddr,
                          password: authPassword,
                        });
                        if (!signInRes?.error && signInRes?.data?.session) {
                          activeSession = signInRes.data.session;
                          console.log('[OTP Verify] onVerified signInWithPassword() fallback succeeded');
                        }
                      } catch {}
                    }

                    if (activeAccount?.user) {
                      localStorage.setItem('qb_scholar_uid', activeAccount.user.uid);
                      setUsername(activeAccount.user.username || verifiedEmailAddr.split('@')[0]);
                      setDisplayName(activeAccount.user.displayName || activeAccount.user.username);
                      onAccountUpdated(activeAccount);
                    }

                    setUnverifiedEmail(null);
                    setAuthPassword('');
                    setActiveModal(null);
                    setSaveBanner('Email verified! Your account is active.');

                    if (handleActiveSession) {
                      handleActiveSession(activeSession, activeAccount);
                    } else if (onSessionEstablished) {
                      onSessionEstablished(activeSession, activeAccount);
                    }
                  } catch (err: any) {
                    console.error('[OTP Verify] Error in onVerified handler:', err);
                    setUnverifiedEmail(null);
                    setActiveModal(null);
                  }
                }}
                onChangeEmail={() => {
                  setUnverifiedEmail(null);
                  setAuthError(null);
                }}
              />
            ) : (
              <div className="space-y-5">
                <div className="flex items-center gap-3.5">
                  <img
                    src="/quickibible-icon.png"
                    alt="QuickiBible Q! Logo"
                    className="w-12 h-12 rounded-2xl object-contain shadow-lg border border-amber-500/30 shrink-0"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {activeModal === 'auth_signup' ? 'Create QuickiBible Account' : 'Sign In to QuickiBible'}
                    </h3>
                    <p className="text-xs text-[#A3A3A3]">
                      {activeModal === 'auth_signup' ? 'Enter your details to register.' : 'Access your saved progress & stats.'}
                    </p>
                  </div>
                </div>

                <form
                  onSubmit={(e) => handleEmailAuthSubmit(e, activeModal === 'auth_signup' ? 'signup' : 'signin')}
                  className="space-y-4"
                >
                  {activeModal === 'auth_signup' && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-[#D4D4D4] mb-1">
                          Username <span className="text-amber-400">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          minLength={3}
                          placeholder="berean_scholar"
                          value={authUsername}
                          onChange={(e) => setAuthUsername(e.target.value)}
                          className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-[#525252] font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#D4D4D4] mb-1">
                          Display Name
                        </label>
                        <input
                          type="text"
                          placeholder="Scripture Scholar"
                          value={authDisplayName}
                          onChange={(e) => setAuthDisplayName(e.target.value)}
                          className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-[#525252]"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-[#D4D4D4] mb-1">
                      Email Address <span className="text-amber-400">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="email"
                        required
                        placeholder="scholar@quickibible.com"
                        value={authEmail}
                        onChange={(e) => setAuthEmail(e.target.value)}
                        className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] pl-9 pr-3 py-2.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-[#525252]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#D4D4D4] mb-1">
                      Password (min 6 characters) <span className="text-amber-400">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="password"
                        required
                        minLength={6}
                        placeholder="••••••••"
                        value={authPassword}
                        onChange={(e) => setAuthPassword(e.target.value)}
                        className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] pl-9 pr-3 py-2.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-[#525252]"
                      />
                    </div>
                  </div>

                  {activeModal === 'auth_signup' && (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-[#0A0A0A] border border-[#262626]">
                      <input
                        type="checkbox"
                        id="modal-consent"
                        checked={consentAgreed}
                        onChange={(e) => setConsentAgreed(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded text-[#A3A3A3] focus:ring-[#525252] border-[#404040] cursor-pointer shrink-0"
                      />
                      <label htmlFor="modal-consent" className="text-xs text-[#D4D4D4] leading-relaxed cursor-pointer select-none">
                        I agree to the{' '}
                        <button
                          type="button"
                          onClick={() => {
                            setLegalSubTab('terms');
                            setActiveModal('legal');
                          }}
                          className="text-[#A3A3A3] underline font-semibold"
                        >
                          Terms of Use
                        </button>{' '}
                        and{' '}
                        <button
                          type="button"
                          onClick={() => {
                            setLegalSubTab('privacy');
                            setActiveModal('legal');
                          }}
                          className="text-[#A3A3A3] underline font-semibold"
                        >
                          Privacy Notice
                        </button>
                        .
                      </label>
                    </div>
                  )}

                  {authError && (
                    <div role="alert" className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300">
                      {authError}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2">
                    {activeModal === 'auth_signin' ? (
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={isSubmittingAuth}
                        className="text-xs font-semibold text-[#A3A3A3] hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                        <span>Forgot Password?</span>
                      </button>
                    ) : <div />}

                    <button
                      type="submit"
                      disabled={isSubmittingAuth}
                      className="px-6 py-3 rounded-xl bg-[#525252] hover:bg-[#404040] disabled:opacity-60 text-white text-xs font-bold transition-all shadow-md cursor-pointer"
                    >
                      {isSubmittingAuth
                        ? 'Please wait...'
                        : activeModal === 'auth_signup'
                        ? 'Create Account'
                        : 'Sign In'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 2. ACCOUNT & SETTINGS MODAL */}
      {activeModal === 'account' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-2xl rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl space-y-6 text-[#FAFAFA] max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#262626] border border-[#525252] flex items-center justify-center text-[#A3A3A3] shrink-0">
                <User className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Account &amp; Settings</h3>
                <p className="text-xs text-[#A3A3A3]">Manage profile information and preferences.</p>
              </div>
            </div>

            {/* Avatar Upload */}
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-[#0A0A0A] border border-[#262626]">
              <div className="w-16 h-16 rounded-xl bg-[#525252] overflow-hidden shrink-0 flex items-center justify-center font-bold text-white">
                {user.avatarUrl ? (
                  <img src={user.avatarUrl} alt={username} className="w-full h-full object-cover" />
                ) : (
                  <span>{(username || 'QB').slice(0, 2).toUpperCase()}</span>
                )}
              </div>
              <div className="space-y-1">
                <div className="text-xs font-semibold text-white">Profile Photo</div>
                <div className="text-[11px] text-[#A3A3A3]">PNG or static GIF, min 98×98px, max 4MB</div>
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold cursor-pointer transition-colors">
                  <Upload className="w-3.5 h-3.5" />
                  <span>{isUploadingAvatar ? 'Uploading...' : 'Upload Photo'}</span>
                  <input
                    type="file"
                    accept="image/png,image/gif"
                    disabled={isUploadingAvatar}
                    onChange={handleAvatarFileChange}
                    className="sr-only"
                    aria-label="Upload profile picture"
                  />
                </label>
              </div>
            </div>

            {avatarError && (
              <div role="alert" className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-400">
                {avatarError}
              </div>
            )}

            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="modal-username" className="block text-xs font-semibold text-[#D4D4D4] mb-1">Username</label>
                  <input
                    id="modal-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white"
                  />
                </div>
                <div>
                  <label htmlFor="modal-display" className="block text-xs font-semibold text-[#D4D4D4] mb-1">Display Name</label>
                  <input
                    id="modal-display"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white"
                  />
                </div>
                <div>
                  <label htmlFor="modal-country" className="block text-xs font-semibold text-[#D4D4D4] mb-1">Country</label>
                  <select
                    id="modal-country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white"
                  >
                    {Object.entries(COUNTRIES).map(([code, info]) => (
                      <option key={code} value={code}>
                        {info.flag} {info.name} ({code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="modal-lang" className="block text-xs font-semibold text-[#D4D4D4] mb-1">Language</label>
                  <select
                    id="modal-lang"
                    value={language}
                    onChange={(e) => onLanguageChange(e.target.value as SupportedLanguage)}
                    className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white"
                  >
                    {Object.values(LANGUAGE_META).map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label} ({l.code.toUpperCase()})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="modal-verse" className="block text-xs font-semibold text-[#D4D4D4] mb-1">Favorite Bible Verse</label>
                  <input
                    id="modal-verse"
                    type="text"
                    value={favoriteVerse}
                    onChange={(e) => setFavoriteVerse(e.target.value)}
                    className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white"
                  />
                </div>
                <div>
                  <label htmlFor="modal-church" className="block text-xs font-semibold text-[#D4D4D4] mb-1">Church / Fellowship</label>
                  <input
                    id="modal-church"
                    type="text"
                    value={church}
                    placeholder="e.g. Grace Fellowship, Manila"
                    onChange={(e) => {
                      setChurch(e.target.value);
                      setChurchOrOrg(e.target.value);
                    }}
                    className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white"
                  />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={hideChurch}
                      onChange={(e) => setHideChurch(e.target.checked)}
                      className="w-3.5 h-3.5 accent-amber-500 rounded bg-[#0A0A0A] border-[#404040]"
                    />
                    <span className="text-[11px] text-[#A3A3A3] hover:text-white">Hide church on public profile</span>
                  </label>
                </div>
                <div>
                  <label htmlFor="modal-city" className="block text-xs font-semibold text-[#D4D4D4] mb-1">City</label>
                  <input
                    id="modal-city"
                    type="text"
                    value={city}
                    placeholder="e.g. Quezon City, London, Dallas"
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white"
                  />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={hideLocation}
                      onChange={(e) => setHideLocation(e.target.checked)}
                      className="w-3.5 h-3.5 accent-amber-500 rounded bg-[#0A0A0A] border-[#404040]"
                    />
                    <span className="text-[11px] text-[#A3A3A3] hover:text-white">Hide location on public profile</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-[#262626]">
                <button
                  type="button"
                  onClick={() => {
                    setAuthError(null);
                    setShowDeleteConfirmModal(true);
                  }}
                  className="px-4 py-2.5 rounded-xl border border-red-500/40 bg-red-500/10 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition-colors"
                >
                  Delete Account
                </button>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setActiveModal(null)}
                    className="px-4 py-2.5 rounded-xl border border-[#404040] text-xs font-semibold text-[#D4D4D4] hover:bg-[#1C1C1C]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingProfile}
                    className="px-6 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] disabled:opacity-60 text-white text-xs font-bold shadow-md transition-colors flex items-center gap-2"
                  >
                    {isSavingProfile ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. AUDIO & NARRATION MODAL */}
      {activeModal === 'audio' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-xl rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl space-y-6 text-[#FAFAFA] max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#1C1C1C] border border-[#525252] flex items-center justify-center text-[#A3A3A3] shrink-0">
                <Volume2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Audio &amp; Narration</h3>
                <p className="text-xs text-[#A3A3A3]">Manage sound effects, background music, and TTS.</p>
              </div>
            </div>

            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="space-y-3">
                <label className="flex items-center justify-between p-3.5 rounded-xl bg-[#0A0A0A] border border-[#262626] cursor-pointer">
                  <div>
                    <div className="text-xs font-semibold text-white">Game Sound Effects (SFX)</div>
                    <div className="text-[11px] text-[#A3A3A3]">Chimes, ticks, and victory sound effects</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={sfxEnabled}
                    onChange={(e) => setSfxEnabled(e.target.checked)}
                    className="w-4 h-4 accent-[#525252] rounded"
                  />
                </label>

                <label className="flex items-center justify-between p-3.5 rounded-xl bg-[#0A0A0A] border border-[#262626] cursor-pointer">
                  <div>
                    <div className="text-xs font-semibold text-white">Ambient Sanctuary Music</div>
                    <div className="text-[11px] text-[#A3A3A3]">Gentle harmonic background pad</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={bgMusicEnabled}
                    onChange={(e) => {
                      setBgMusicEnabled(e.target.checked);
                      soundEngine.toggleBackgroundMusic(e.target.checked, bgMusicVolume / 100);
                    }}
                    className="w-4 h-4 accent-[#525252] rounded"
                  />
                </label>

                <div className="p-3.5 rounded-xl bg-[#0A0A0A] border border-[#262626] space-y-2">
                  <label htmlFor="modal-vol" className="block text-xs font-semibold text-[#D4D4D4]">
                    Background Music Volume ({bgMusicVolume}%)
                  </label>
                  <input
                    id="modal-vol"
                    type="range"
                    min={0}
                    max={100}
                    value={bgMusicVolume}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setBgMusicVolume(v);
                      if (bgMusicEnabled) soundEngine.toggleBackgroundMusic(true, v / 100);
                    }}
                    className="w-full accent-[#525252]"
                  />
                </div>

                <label className="flex items-center justify-between p-3.5 rounded-xl bg-[#0A0A0A] border border-[#262626] cursor-pointer">
                  <div>
                    <div className="text-xs font-semibold text-white">Enable Scripture Narration (TTS)</div>
                    <div className="text-[11px] text-[#A3A3A3]">Read questions aloud in {language.toUpperCase()}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={narrationEnabled}
                    onChange={(e) => {
                      setNarrationEnabled(e.target.checked);
                      soundEngine.narrationEnabled = e.target.checked;
                    }}
                    className="w-4 h-4 accent-[#525252] rounded"
                  />
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="modal-voice" className="block text-xs font-semibold text-[#D4D4D4] mb-1">Narrator Voice</label>
                    <select
                      id="modal-voice"
                      value={narrationVoice}
                      onChange={(e) => setNarrationVoice(e.target.value)}
                      className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white"
                    >
                      <option value="Kore">Kore — Warm Scholar</option>
                      <option value="Puck">Puck — Quiz Host</option>
                      <option value="Charon">Charon — Reverent</option>
                      <option value="Zephyr">Zephyr — Storyteller</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="modal-speed" className="block text-xs font-semibold text-[#D4D4D4] mb-1">Narration Speed</label>
                    <select
                      id="modal-speed"
                      value={narrationSpeed}
                      onChange={(e) => setNarrationSpeed(e.target.value)}
                      className="w-full rounded-xl border border-[#404040] bg-[#0A0A0A] px-3.5 py-2.5 text-xs text-white"
                    >
                      <option value="0.85">0.85x (Relaxed)</option>
                      <option value="1.0">1.0x (Normal)</option>
                      <option value="1.15">1.15x (Brisk)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-[#262626]">
                <button
                  type="button"
                  onClick={() => {
                    soundEngine.narrationVoice = narrationVoice;
                    soundEngine.narrationSpeed = Number(narrationSpeed) || 1.0;
                    soundEngine.speakText('Thy word is a lamp unto my feet, and a light unto my path. Psalm 119:105.', { language, force: true });
                  }}
                  className="px-4 py-2 rounded-xl border border-[#404040] text-xs font-semibold text-[#D4D4D4] hover:bg-[#1C1C1C] flex items-center gap-1.5"
                >
                  <Volume2 className="w-3.5 h-3.5 text-[#A3A3A3]" />
                  <span>Test Narration</span>
                </button>
                <button
                  type="submit"
                  disabled={isSavingProfile}
                  className="px-6 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] disabled:opacity-60 text-white text-xs font-bold shadow-md transition-colors flex items-center gap-2"
                >
                  {isSavingProfile ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    'Save Audio Preferences'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. LANGUAGE MODAL */}
      {activeModal === 'language' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-md rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl space-y-6 text-[#FAFAFA]">
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#262626] border border-[#525252] flex items-center justify-center text-[#A3A3A3] shrink-0">
                <Globe className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Language Preferences</h3>
                <p className="text-xs text-[#A3A3A3]">Select interface &amp; Bible language.</p>
              </div>
            </div>

            <div className="space-y-2">
              {Object.values(LANGUAGE_META).map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => {
                    onLanguageChange(l.code);
                    soundEngine.playClick();
                    setActiveModal(null);
                  }}
                  className={`w-full p-3.5 rounded-xl border flex items-center justify-between transition-all cursor-pointer ${
                    language === l.code
                      ? 'border-[#525252] bg-[#262626] text-white font-bold'
                      : 'border-[#262626] bg-[#0A0A0A] text-[#D4D4D4] hover:bg-[#1C1C1C]'
                  }`}
                >
                  <span>{l.label} — {l.nativeLabel}</span>
                  <span className="text-xs font-mono uppercase text-[#A3A3A3]">{l.code}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 5. ACHIEVEMENTS & BADGES MODAL */}
      {activeModal === 'achievements' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-2xl rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl space-y-6 text-[#FAFAFA] max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Badges &amp; Achievements</h3>
                <p className="text-xs text-[#A3A3A3]">{unlockedBadges.length} of {SEED_ACHIEVEMENTS.length} unlocked</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SEED_ACHIEVEMENTS.map((ach) => {
                const isUnlocked = unlockedBadges.includes(ach.code);
                return (
                  <div
                    key={ach.code}
                    className={`p-4 rounded-2xl border transition-all ${
                      isUnlocked
                        ? 'border-amber-500/40 bg-amber-500/10 text-white'
                        : 'border-[#262626] bg-[#0A0A0A] text-[#A3A3A3] opacity-70'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-bold">{ach.title}</span>
                      <span className="font-mono text-xs text-amber-400 font-bold">+{ach.xpReward} XP</span>
                    </div>
                    <p className="text-xs text-[#D4D4D4]">{ach.description}</p>
                    <div className="mt-2 text-[10px] font-mono text-[#A3A3A3] font-semibold uppercase">
                      {isUnlocked ? 'Unlocked Milestone' : 'Locked'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 6. SCHOLAR PROGRESSION MODAL */}
      {activeModal === 'progression' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-lg rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl space-y-6 text-[#FAFAFA]">
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#262626] border border-[#525252] flex items-center justify-center text-[#A3A3A3] shrink-0">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Scholar Progression</h3>
                <p className="text-xs text-[#A3A3A3] font-mono">{levelInfo.summaryText} · {levelInfo.rankTitle}</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Progress Bar inside Progression Modal */}
              <div className="p-4 rounded-2xl bg-[#0A0A0A] border border-[#262626] space-y-2">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="font-bold text-white">{levelInfo.summaryText}</span>
                  <span className="text-[#A3A3A3]">{levelInfo.progressPercent}%</span>
                </div>
                <div className="w-full h-2.5 rounded-full bg-[#1C1C1C] overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#FF8A00] to-[#EA580C] rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, Math.max(4, levelInfo.progressPercent))}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-[#0A0A0A] border border-[#262626]">
                  <div className="text-xs text-[#A3A3A3]">Total Games Played</div>
                  <div
                    className={`text-2xl font-mono font-bold mt-1 tabular-nums ${
                      formatDisplayNumber(stats.totalGames || 0).isZero
                        ? 'text-[#737373]'
                        : 'text-white'
                    }`}
                  >
                    {formatDisplayNumber(stats.totalGames || 0).text}
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-[#0A0A0A] border border-[#262626]">
                  <div className="text-xs text-[#A3A3A3]">Questions Seen</div>
                  <div
                    className={`text-2xl font-mono font-bold mt-1 tabular-nums ${
                      formatDisplayNumber(stats.totalQuestionsAnswered || 0).isZero
                        ? 'text-[#737373]'
                        : 'text-[#A3A3A3]'
                    }`}
                  >
                    {formatDisplayNumber(stats.totalQuestionsAnswered || 0).text}
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-[#0A0A0A] border border-[#262626]">
                  <div className="text-xs text-[#A3A3A3]">Best Streak</div>
                  <div
                    className={`text-2xl font-mono font-bold mt-1 tabular-nums ${
                      formatDisplayNumber(stats.bestWinStreak || 0).isZero
                        ? 'text-[#737373]'
                        : 'text-amber-400'
                    }`}
                  >
                    {formatDisplayNumber(stats.bestWinStreak || 0).text}
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-[#0A0A0A] border border-[#262626]">
                  <div className="text-xs text-[#A3A3A3]">Total XP</div>
                  <div
                    className={`text-2xl font-mono font-bold mt-1 tabular-nums ${
                      formatDisplayNumber(user.xp || 0).isZero
                        ? 'text-[#737373]'
                        : 'text-emerald-400'
                    }`}
                  >
                    {formatDisplayNumber(user.xp || 0).text}
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-[#0A0A0A] border border-[#262626] col-span-2">
                  <div className="text-xs text-[#A3A3A3]">Lifetime Accuracy</div>
                  <div
                    className={`text-lg font-mono font-bold mt-1 tabular-nums ${
                      lifetimeAccInfo.unlocked ? 'text-emerald-400' : 'text-[#737373]'
                    }`}
                  >
                    {lifetimeAccInfo.label}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6.5 FRIENDS & FELLOWSHIP MODAL */}
      {activeModal === 'friends' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-xl rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl space-y-6 text-[#FAFAFA] max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-500 shrink-0">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Fellowship &amp; Friends</h3>
                <p className="text-xs text-[#A3A3A3]">Connect and study Scripture together</p>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex items-center gap-2 p-1 rounded-xl bg-[#0A0A0A] border border-[#262626]">
              <button
                type="button"
                onClick={() => setFriendsSubTab('friends')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                  friendsSubTab === 'friends'
                    ? 'bg-[#262626] text-white shadow-sm'
                    : 'text-[#A3A3A3] hover:text-white'
                }`}
              >
                Friends ({friendsList.length})
              </button>
              <button
                type="button"
                onClick={() => setFriendsSubTab('requests')}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                  friendsSubTab === 'requests'
                    ? 'bg-[#262626] text-white shadow-sm'
                    : 'text-[#A3A3A3] hover:text-white'
                }`}
              >
                <span>Requests</span>
                {friendRequests.length > 0 && (
                  <span className="px-1.5 py-0.2 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-mono border border-amber-500/40">
                    {friendRequests.length}
                  </span>
                )}
              </button>
            </div>

            {isLoadingFriends ? (
              <div className="py-12 flex flex-col items-center justify-center gap-2 text-xs text-[#A3A3A3]">
                <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                <span>Loading fellowship...</span>
              </div>
            ) : friendsSubTab === 'friends' ? (
              <div className="space-y-3">
                {friendsList.length === 0 ? (
                  <div className="py-10 text-center rounded-2xl bg-[#0A0A0A] border border-[#262626] p-6 space-y-2">
                    <Users className="w-8 h-8 text-[#525252] mx-auto mb-2" />
                    <div className="text-sm font-semibold text-white">No Friends Added Yet</div>
                    <p className="text-xs text-[#A3A3A3] max-w-sm mx-auto">
                      Explore the Community Live Feed or Leaderboard and tap "Add Friend" on fellow scholars to build your fellowship!
                    </p>
                  </div>
                ) : (
                  friendsList.map((friend) => {
                    const countryInfo = COUNTRIES[friend.country];
                    return (
                      <div
                        key={friend.friendshipId}
                        className="p-3.5 rounded-2xl bg-[#0A0A0A] border border-[#262626] flex items-center justify-between gap-3 hover:border-[#404040] transition-colors"
                      >
                        <div
                          className="flex items-center gap-3 min-w-0 cursor-pointer group"
                          onClick={() => {
                            if (onOpenUserProfile) onOpenUserProfile(friend.friendUid);
                          }}
                        >
                          {friend.avatarUrl ? (
                            <img
                              src={friend.avatarUrl}
                              alt={friend.displayName}
                              className="w-10 h-10 rounded-full object-cover border border-[#404040] shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-[#1C1C1C] border border-[#404040] flex items-center justify-center text-xs font-bold text-amber-400 shrink-0">
                              {(friend.displayName || friend.username || 'S')[0]?.toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-white group-hover:text-amber-400 transition-colors truncate flex items-center gap-1.5">
                              <span>{friend.displayName}</span>
                              {countryInfo && <span title={countryInfo.name}>{countryInfo.flag}</span>}
                            </div>
                            <div className="text-[11px] font-mono text-[#A3A3A3] truncate">
                              @{friend.username}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {onOpenUserProfile && (
                            <button
                              type="button"
                              onClick={() => onOpenUserProfile(friend.friendUid)}
                              className="px-2.5 py-1.5 rounded-lg border border-[#333333] hover:border-[#525252] text-[11px] text-[#D4D4D4] hover:text-white transition-colors cursor-pointer"
                            >
                              Profile
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleUnfriend(friend.friendshipId)}
                            className="px-2.5 py-1.5 rounded-lg border border-red-500/30 hover:bg-red-500/10 text-[11px] text-red-400 transition-colors cursor-pointer"
                          >
                            Unfriend
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {friendRequests.length === 0 ? (
                  <div className="py-10 text-center rounded-2xl bg-[#0A0A0A] border border-[#262626] p-6 space-y-2">
                    <UserCheck className="w-8 h-8 text-[#525252] mx-auto mb-2" />
                    <div className="text-sm font-semibold text-white">No Pending Requests</div>
                    <p className="text-xs text-[#A3A3A3]">
                      When another scholar sends you a friend request, it will appear here for you to accept or decline.
                    </p>
                  </div>
                ) : (
                  friendRequests.map((req) => {
                    const countryInfo = COUNTRIES[req.senderCountry];
                    return (
                      <div
                        key={req.id}
                        className="p-3.5 rounded-2xl bg-[#0A0A0A] border border-[#262626] flex items-center justify-between gap-3"
                      >
                        <div
                          className="flex items-center gap-3 min-w-0 cursor-pointer group"
                          onClick={() => {
                            if (onOpenUserProfile) onOpenUserProfile(req.senderUid);
                          }}
                        >
                          {req.senderAvatarUrl ? (
                            <img
                              src={req.senderAvatarUrl}
                              alt={req.senderDisplayName}
                              className="w-10 h-10 rounded-full object-cover border border-[#404040] shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-[#1C1C1C] border border-[#404040] flex items-center justify-center text-xs font-bold text-amber-400 shrink-0">
                              {(req.senderDisplayName || req.senderUsername || 'S')[0]?.toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-white group-hover:text-amber-400 transition-colors truncate flex items-center gap-1.5">
                              <span>{req.senderDisplayName}</span>
                              {countryInfo && <span title={countryInfo.name}>{countryInfo.flag}</span>}
                            </div>
                            <div className="text-[11px] font-mono text-[#A3A3A3] truncate">
                              @{req.senderUsername}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleRespondFriendRequest(req.id, 'accept')}
                            className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-[11px] font-bold text-white transition-colors cursor-pointer flex items-center gap-1"
                          >
                            <Check className="w-3.5 h-3.5" />
                            <span>Accept</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRespondFriendRequest(req.id, 'decline')}
                            className="px-2.5 py-1.5 rounded-lg border border-[#404040] hover:bg-[#262626] text-[11px] text-[#A3A3A3] hover:text-white transition-colors cursor-pointer"
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 7. ADMIN CONSOLE MODAL */}
      {activeModal === 'admin' && isAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-4xl rounded-3xl bg-[#141414] border border-amber-900/40 p-6 sm:p-8 shadow-2xl space-y-6 text-[#FAFAFA] max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Administrator Console</h3>
                <p className="text-xs text-[#A3A3A3]">Validated Question Pool &amp; Security Governance</p>
              </div>
            </div>

            {loadingAdmin ? (
              <div className="py-12 text-center text-sm text-[#A3A3A3]">Loading admin telemetry...</div>
            ) : adminData ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-[#0A0A0A] border border-[#262626]">
                    <div className="text-xs text-[#A3A3A3]">Verified Questions</div>
                    <div className="text-2xl font-mono font-bold text-white mt-1">{adminData.questions?.length || 0}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-[#0A0A0A] border border-[#262626]">
                    <div className="text-xs text-[#A3A3A3]">Registered Scholars</div>
                    <div className="text-2xl font-mono font-bold text-[#A3A3A3] mt-1">{adminData.users?.length || 0}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-[#0A0A0A] border border-[#262626]">
                    <div className="text-xs text-[#A3A3A3]">Pool Expansions</div>
                    <div className="text-2xl font-mono font-bold text-emerald-400 mt-1">{adminData.generationLogs?.length || 0}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-[#0A0A0A] border border-[#262626]">
                    <div className="text-xs text-[#A3A3A3]">LIVE Events</div>
                    <div className="text-2xl font-mono font-bold text-amber-400 mt-1">{adminData.liveEvents?.length || 0}</div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-[#262626] text-[#A3A3A3] uppercase">
                        <th className="py-2.5 px-3">Code</th>
                        <th className="py-2.5 px-3">Question &amp; Reference</th>
                        <th className="py-2.5 px-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#262626]">
                      {adminData.questions?.slice(0, 10).map((q: any) => (
                        <tr key={q.id}>
                          <td className="py-3 px-3 font-mono text-[#A3A3A3]">{q.questionCode}</td>
                          <td className="py-3 px-3 font-semibold text-white">{q.questionText}</td>
                          <td className="py-3 px-3 font-mono text-amber-400">{q.validationStatus}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* 8. LEGAL DOCUMENTS & POLICIES MODAL */}
      {activeModal === 'legal' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="relative w-full max-w-3xl rounded-3xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-2xl space-y-6 text-[#FAFAFA] max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 p-2 rounded-xl text-[#A3A3A3] hover:text-white hover:bg-[#1C1C1C] transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center justify-between border-b border-[#262626] pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#262626] text-[#A3A3A3] flex items-center justify-center">
                  <Scale className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-white">Legal Documents &amp; Policies</h3>
              </div>

              <div className="flex items-center gap-1 bg-[#0A0A0A] p-1 rounded-xl border border-[#262626]">
                <button
                  type="button"
                  onClick={() => setLegalSubTab('terms')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    legalSubTab === 'terms' ? 'bg-[#525252] text-white' : 'text-[#A3A3A3] hover:text-white'
                  }`}
                >
                  Terms
                </button>
                <button
                  type="button"
                  onClick={() => setLegalSubTab('privacy')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    legalSubTab === 'privacy' ? 'bg-[#525252] text-white' : 'text-[#A3A3A3] hover:text-white'
                  }`}
                >
                  Privacy
                </button>
                <button
                  type="button"
                  onClick={() => setLegalSubTab('guidelines')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    legalSubTab === 'guidelines' ? 'bg-[#525252] text-white' : 'text-[#A3A3A3] hover:text-white'
                  }`}
                >
                  Guidelines
                </button>
              </div>
            </div>

            <div className="py-2">
              {legalSubTab === 'terms' && <TermsOfUseView onBack={() => setActiveModal(null)} />}
              {legalSubTab === 'privacy' && <PrivacyNoticeView onBack={() => setActiveModal(null)} />}
              {legalSubTab === 'guidelines' && <CommunityGuidelinesView onBack={() => setActiveModal(null)} />}
            </div>
          </div>
        </div>
      )}

      {/* DELETE ACCOUNT CONFIRMATION MODAL */}
      {showDeleteConfirmModal && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-md rounded-3xl bg-[#141414] border border-[#262626] p-6 shadow-2xl space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h4 className="text-base font-bold text-white">Delete Account</h4>
                <p className="text-xs text-[#D4D4D4] leading-relaxed">
                  Are you sure? Your score, rankings, and progression data will be permanently deleted.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#262626]">
              <button
                type="button"
                disabled={isDeletingAccount}
                onClick={() => setShowDeleteConfirmModal(false)}
                className="px-4 py-2 rounded-xl border border-[#404040] text-xs font-semibold text-[#D4D4D4] hover:bg-[#1C1C1C]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeletingAccount}
                onClick={handleConfirmDeleteAccount}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold"
              >
                {isDeletingAccount ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
